/**
 * providerCtas.ts — sponsored provider referral cards on listing detail.
 *
 * Three placements, each a native card with a "Sponsored" pill:
 *   deal_analysis    → insurance  (under Deal Analysis)
 *   financing        → lender     (beside Financing Scenarios)
 *   condition_report → inspector  (next to HomeCheck)
 *
 * Partner URLs are server-side config (GET /config/provider-ctas, backed
 * by PROVIDER_*_URL env vars) — never hardcoded here. renderDetail emits
 * an empty slot per placement; mountProviderCtas() fills each slot whose
 * URL is configured and removes the rest, so an unconfigured card renders
 * nothing at all.
 */

import { api } from './api.js';
import { trackEvent } from './analytics.js';
import type { ProviderCtaPlacement, ProviderCtaProvider } from './analytics.js';
import { escapeHtml } from './ui.js';

/** Keys mirror GET /config/provider-ctas: one per provider, plus the
 *  portfolio renewal nudge's URL (server falls back to `insurance`). */
export type ProviderCtaConfigKey = ProviderCtaProvider | 'insurance_renewal';
type ProviderCtaConfig = Partial<Record<ProviderCtaConfigKey, string | null>>;

/** The three listing-detail placements this module renders itself; the
 *  portfolio renewal nudge (portfolio.ts) only borrows the config + styles. */
type DetailCtaPlacement = Exclude<ProviderCtaPlacement, 'portfolio_renewal'>;

interface ProviderCtaCopy {
  provider: ProviderCtaProvider;
  icon: string;
  title: string;
  body: string;
  button: string;
}

const CTA_COPY: Record<DetailCtaPlacement, ProviderCtaCopy> = {
  deal_analysis: {
    provider: 'insurance',
    icon: 'shield',
    title: 'Protect this deal',
    body: 'Landlord insurance quotes for investors, in about 5 minutes.',
    button: 'Get an insurance quote',
  },
  financing: {
    provider: 'lender',
    icon: 'briefcase',
    title: 'Finance this deal',
    body: 'Hard-money and DSCR lenders built for investors.',
    button: 'Compare investor lenders',
  },
  condition_report: {
    provider: 'inspector',
    icon: 'check-square',
    title: 'Verify before you buy',
    body: 'Licensed Houston inspectors for investment properties.',
    button: 'Find an inspector',
  },
};

export const SPONSORED_DISCLOSURE = 'Paid partnership — SwipeHome may earn a referral fee.';

let _configPromise: Promise<ProviderCtaConfig> | null = null;

function loadConfig(): Promise<ProviderCtaConfig> {
  if (!_configPromise) {
    _configPromise = api<ProviderCtaConfig>('GET', '/config/provider-ctas').catch(() => {
      // Don't cache a failure — the next detail view retries.
      _configPromise = null;
      return {};
    });
  }
  return _configPromise;
}

/** Resolved partner URL for one config key, or null when unconfigured
 *  (→ render nothing). Shared with the portfolio renewal nudge. */
export async function getProviderCtaUrl(key: ProviderCtaConfigKey): Promise<string | null> {
  const config = await loadConfig();
  return safeUrl(config[key]);
}

/** Only absolute http(s) URLs are rendered (the backend enforces this too). */
function safeUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

/** Empty mount point for one placement — invisible until filled. */
export function providerCtaSlot(placement: DetailCtaPlacement): string {
  return `<div class="provider-cta-slot" data-cta-placement="${placement}"></div>`;
}

function renderCard(placement: DetailCtaPlacement): string {
  const c = CTA_COPY[placement];
  return `
    <div class="provider-cta-card" data-provider="${c.provider}">
      <span class="provider-cta-pill">Sponsored</span>
      <div class="provider-cta-head">
        <span class="provider-cta-icon"><i data-feather="${c.icon}" width="16"></i></span>
        <div>
          <div class="provider-cta-title">${escapeHtml(c.title)}</div>
          <div class="provider-cta-body">${escapeHtml(c.body)}</div>
        </div>
      </div>
      <!-- Sponsored CTAs never use .btn-primary: primary is reserved for real
           app actions (Recalculate, Save), so paid placements stay quieter. -->
      <a class="btn btn-secondary btn-block provider-cta-btn" target="_blank" rel="noopener noreferrer sponsored">
        ${escapeHtml(c.button)}
        <i data-feather="external-link" width="14"></i>
      </a>
      <div class="provider-cta-disclosure">${escapeHtml(SPONSORED_DISCLOSURE)}</div>
    </div>
  `;
}

/** Fill (or remove) every provider CTA slot currently in the DOM. */
export async function mountProviderCtas(listingId?: string): Promise<void> {
  const config = await loadConfig();
  document.querySelectorAll<HTMLElement>('.provider-cta-slot').forEach((slot) => {
    const placement = slot.dataset.ctaPlacement as DetailCtaPlacement | undefined;
    const copy = placement ? CTA_COPY[placement] : undefined;
    const url = copy ? safeUrl(config[copy.provider]) : null;
    if (!placement || !copy || !url) {
      slot.remove();
      return;
    }
    slot.innerHTML = renderCard(placement);
    const btn = slot.querySelector<HTMLAnchorElement>('.provider-cta-btn');
    if (!btn) return;
    // Set via the DOM property, not string interpolation, so the URL can
    // never break out of the attribute.
    btn.href = url;
    btn.addEventListener('click', () => {
      trackEvent('provider_cta_click', {
        placement,
        provider: copy.provider,
        ...(listingId ? { listing_id: listingId } : {}),
      });
    });
  });
  if (window.feather) window.feather.replace();
}
